import { Alert, Button, Card, Modal, Space, Tag, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import type {
    FindContractSpecialsItem,
    SpecialZoneContractPagingRow,
    SpecialZoneContractPriceAdjustLog
} from "@zhihaoscm/entity";
import {
    ZONE_CONTRACT_GOODS,
    ZONE_CONTRACT_RESERVATION_ORDER_STATE,
    ZONE_CONTRACT_SPECIAL_ZONE,
    ZONE_CONTRACT_STATE
} from "@zhihaoscm/meta";
import { useUserStore } from "@/views/stores";
import AssignDialog, { type assignSubmitData } from "@/views/components/AssignDialog/AssignDialog";
import { BaseItem as ZhBaseItem, Grid as ZhGrid, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import type { ZoneContractReservationResult, ZoneContractReservationRow } from "./zoneContractReactTypes";
import { zoneContractApi } from "./zoneContractReactApi";
import {
    goodsIntroByGoodsCode,
    reservationOrderStateLabel,
    displayApiDateTimeUnknown
} from "../zoneReservationOrder/zoneReservationDisplay";
import { displayZoneContractCustomerText } from "./zoneContractCustomerLabel";
import { mapContractSpecialsToOfficers } from "./zoneContractAssign";

export interface ZoneContractDetailPageProps {
    contractId?: string;
    loadDetail?: (id: number) => Promise<SpecialZoneContractPagingRow>;
    loadReservations?: (params: {
        contractId: number;
        page: number;
        size: number;
    }) => Promise<ZoneContractReservationResult>;
    loadPriceLogs?: (params: {
        contractId: number;
        page: number;
        size: number;
    }) => Promise<{ list?: SpecialZoneContractPriceAdjustLog[]; total?: number }>;
    cancelContract?: (id: number) => Promise<void>;
    permissions?: string[];
}

const loadDetailDefault = (id: number) => zoneContractApi.detail(id);
const loadReservationsDefault = (params: { contractId: number; page: number; size: number }) =>
    zoneContractApi.reservations(params);
const loadPriceLogsDefault = (params: { contractId: number; page: number; size: number }) =>
    zoneContractApi.priceLogs(params);

function reservationCore(row: ZoneContractReservationRow) {
    return row.specialZoneReservationOrder || row;
}

/** 预约单接口一行可能携带多个物料，详情表按旧 Vue 表格拆成一行一物料。 */
function flattenReservationRows(rows: ZoneContractReservationRow[]): ZoneContractReservationRow[] {
    return rows.flatMap(row => {
        const goods = row.specialZoneReservationOrderGoods;
        if (!Array.isArray(goods) || goods.length <= 1) return [row];
        return goods.map(item => ({ ...row, specialZoneReservationOrderGoods: [item] }));
    });
}

function isCancelledState(value: unknown): boolean {
    // 预约单和合同使用不同状态枚举；Vue 端预约取消状态为 5，不能复用合同取消状态 2。
    if (typeof value !== "string" && typeof value !== "number") return false;
    return ZONE_CONTRACT_RESERVATION_ORDER_STATE.CANCELLED.match(value);
}

function contractStateLabel(value: unknown): string {
    const code = Number(value);
    return Number.isFinite(code) ? ZONE_CONTRACT_STATE.getIntroByCode(code) || "--" : "--";
}

function numberValue(value: unknown): number | undefined {
    if (value == null || value === "") return undefined;
    const result = Number(value);
    return Number.isFinite(result) ? result : undefined;
}

function formatNumber(value: unknown, maximumFractionDigits = 6): string {
    const result = numberValue(value);
    return result == null ? "--" : result.toLocaleString("zh-CN", { maximumFractionDigits, minimumFractionDigits: 0 });
}

function reservationGoods(row: ZoneContractReservationRow): Record<string, unknown> {
    const value = row.specialZoneReservationOrderGoods;
    return Array.isArray(value) && value.length && typeof value[0] === "object"
        ? (value[0] as Record<string, unknown>)
        : {};
}

function reservationValue(row: ZoneContractReservationRow, key: string): unknown {
    return (reservationCore(row) as Record<string, unknown>)[key] ?? row[key];
}

/** 合同详情：并行加载合同和预约明细，价格日志严格在显式点击后请求。 */
export function ZoneContractDetailPage({
    contractId: contractIdProp,
    loadDetail = loadDetailDefault,
    loadReservations = loadReservationsDefault,
    loadPriceLogs = loadPriceLogsDefault,
    cancelContract = zoneContractApi.cancel,
    permissions
}: ZoneContractDetailPageProps) {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_ZONE_CONTRACT_MANAGE");
    const canDeal = currentPermissions.includes("ROLE_ZONE_CONTRACT_DEAL");
    // Vue 详情路由通过 query.id 传递合同编号，同时兼容 React 路径参数和测试注入值。
    const id = Number(contractIdProp ?? params.id ?? new URLSearchParams(location.search).get("id"));
    const [detail, setDetail] = useState<SpecialZoneContractPagingRow>();
    const [reservations, setReservations] = useState<ZoneContractReservationRow[]>([]);
    const [error, setError] = useState<string>();
    const [cancelOpen, setCancelOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [priceOpen, setPriceOpen] = useState(false);
    const [logs, setLogs] = useState<SpecialZoneContractPriceAdjustLog[]>([]);
    const [officers, setOfficers] = useState<FindContractSpecialsItem[]>([]);
    const [handlerId, setHandlerId] = useState<number>();
    const assignOfficers = useMemo(() => mapContractSpecialsToOfficers(officers), [officers]);

    useEffect(() => {
        if (!Number.isFinite(id) || id <= 0) {
            setError("缺少有效的合同 ID");
            return;
        }
        let disposed = false;
        void Promise.all([loadDetail(id), loadReservations({ contractId: id, page: 1, size: 1000 })])
            .then(([contract, booking]) => {
                if (!disposed) {
                    setDetail(contract);
                    setReservations(flattenReservationRows(booking.list || []));
                }
            })
            .catch(() => {
                if (!disposed) setError("加载合同详情失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail, loadReservations]);

    const core = detail?.specialZoneContract;
    const cancelled = isCancelledState(core?.state);
    const goods = (detail?.specialZoneContractGoods || []).filter(
        row => row.goodsCode !== 0 && row.goodsName !== "汇总"
    );
    const goodsTotalTon = goods.reduce((total, row) => total + Number(row.totalTon || 0), 0);
    // 合同预约明细沿用 Vue 字段映射，避免改变预约单状态和船舶展示文案。
    const reservationColumns: TableColumnProps<ZoneContractReservationRow>[] = [
        { label: "预约单编号", minWidth: 140, prop: "id", render: ({ row }) => reservationCore(row).id || "--" },
        {
            label: "船名",
            minWidth: 110,
            prop: "ship",
            render: ({ row }) =>
                reservationCore(row).shipName ||
                (row.ship as { cnname?: string; name?: string } | undefined)?.cnname ||
                "--"
        },
        {
            label: "船运单编号",
            minWidth: 130,
            prop: "transport",
            render: ({ row }) =>
                String(reservationCore(row).transportOrderShipId || reservationCore(row).requirementPlatId || "--")
        }
    ];
    // 单价调整日志只在用户打开弹窗后加载，表格保留旧 Vue 的价格和时间列。
    const logColumns: TableColumnProps<SpecialZoneContractPriceAdjustLog>[] = [
        {
            label: "物料",
            prop: "goods",
            render: ({ row }) => row.goodsName || ZONE_CONTRACT_GOODS.getIntroByCode(row.goodsCode) || "--"
        },
        {
            label: "原单价（元）",
            minWidth: 110,
            prop: "oldUnitPrice",
            render: ({ row }) => formatNumber(row.oldUnitPrice)
        },
        {
            label: "新单价（元）",
            minWidth: 110,
            prop: "newUnitPrice",
            render: ({ row }) => formatNumber(row.newUnitPrice)
        },
        {
            label: "单价生效时间",
            minWidth: 160,
            prop: "effectiveTime",
            render: ({ row }) => displayApiDateTimeUnknown(row.effectiveTime)
        },
        { label: "操作人", minWidth: 115, prop: "operatorName", render: ({ row }) => row.operatorName || "--" },
        {
            label: "操作时间",
            minWidth: 150,
            prop: "operationTime",
            render: ({ row }) => displayApiDateTimeUnknown(row.operationTime)
        }
    ];

    async function openAssign() {
        try {
            setOfficers(await zoneContractApi.specials());
            setHandlerId(core?.handlerId || undefined);
            setAssignOpen(true);
        } catch {
            message.error("加载合同专员列表失败");
        }
    }
    async function submitAssign({ officerId }: assignSubmitData) {
        if (core?.id == null || officerId == null) return;
        try {
            await zoneContractApi.assign(core.id, Number(officerId));
            setAssignOpen(false);
            setHandlerId(undefined);
            message.success("指派成功");
        } catch {
            message.error("指派失败");
        }
    }

    /** 取消合同前重新按旧 Vue 的 100 条分页校验，避免详情首屏数据不足导致误取消。 */
    async function hasActiveReservation(): Promise<boolean> {
        if (core?.id == null) return false;
        let page = 1;
        while (true) {
            const result = await loadReservations({ contractId: core.id, page, size: 100 });
            const rows = result.list || [];
            if (rows.some(row => !isCancelledState(reservationValue(row, "state")))) return true;
            if (rows.length < 100) return false;
            page += 1;
        }
    }

    async function requestCancel() {
        if (core?.id == null) return;
        let blocked: boolean;
        try {
            blocked = await hasActiveReservation();
        } catch {
            message.error("校验失败，请稍后重试");
            return;
        }
        if (blocked) {
            message.warning("合同下已有预约单，无法取消，请先取消预约单！");
            return;
        }
        setCancelOpen(true);
    }

    async function confirmCancel() {
        if (core?.id == null) return;
        try {
            await cancelContract(core.id);
            setCancelOpen(false);
            setDetail(current =>
                current ? { ...current, specialZoneContract: { ...current.specialZoneContract, state: 2 } } : current
            );
        } catch {
            message.error("取消合同失败");
        }
    }
    async function openPriceLogs() {
        if (core?.id == null) return;
        setPriceOpen(true);
        try {
            const result = await loadPriceLogs({ contractId: core.id, page: 1, size: 20 });
            setLogs(result.list || []);
        } catch {
            message.error("加载单价调整日志失败");
        }
    }

    if (error) return <Alert message={error} type="error" />;
    return (
        <section aria-label="zone-contract-detail-page">
            {!detail ? (
                <Card loading />
            ) : (
                <>
                    <div className="section-card zone-contract-header">
                        <div className="section-title mb-0">合同信息</div>
                        <ZhGrid cols={3} gap={[20, 16]}>
                            <ZhBaseItem label="合同编号" value={core?.contractCode || "--"} />
                            <ZhBaseItem
                                label="采购用户"
                                value={
                                    detail.businessCustomer?.institutionName ||
                                    detail.businessCustomer?.applyState != null
                                        ? displayZoneContractCustomerText(detail.businessCustomer)
                                        : [
                                              detail.businessCustomer?.realName ||
                                                  detail.businessCustomer?.institutionName,
                                              detail.businessCustomer?.mobile,
                                              detail.businessCustomer?.code
                                          ]
                                              .filter(Boolean)
                                              .join(" | ") || "--"
                                }
                            />
                            <ZhBaseItem
                                label="专区名称"
                                value={
                                    ZONE_CONTRACT_SPECIAL_ZONE.getIntroByCode(core?.specialZoneId) ||
                                    String(core?.specialZoneId ?? "--")
                                }
                            />
                            <ZhBaseItem
                                label="合同日期"
                                value={`${core?.pickStartDate || "--"} ～ ${core?.pickEndDate || "--"}`}
                            />
                            <ZhBaseItem label="签订日期" value={core?.signDate || "--"} />
                            <ZhBaseItem label="创建时间" value={displayApiDateTimeUnknown(core?.createdTime)} />
                            <ZhBaseItem label="合同专员" value={core?.handlerName || "--"} />
                            <ZhBaseItem
                                label="专区余额"
                                value={
                                    detail.specialZoneAccount?.balanceAmount == null
                                        ? "--"
                                        : String(detail.specialZoneAccount.balanceAmount)
                                }
                            />
                            <ZhBaseItem
                                label="预占金额"
                                value={
                                    core?.occAmount == null && detail.specialZoneAccount?.occAmount == null
                                        ? "--"
                                        : String(core?.occAmount ?? detail.specialZoneAccount?.occAmount)
                                }
                            />
                        </ZhGrid>
                        {core ? (
                            <Tag color={cancelled ? "default" : "success"}>{contractStateLabel(core.state)}</Tag>
                        ) : null}
                    </div>
                    <div className="section-card zone-contract-material">
                        <div className="zone-contract-material__title">
                            <Typography.Title level={5}>物料信息</Typography.Title>
                            <Button type="link" onClick={() => void openPriceLogs()}>
                                单价调整日志
                            </Button>
                        </div>
                        <ZhTable
                            border
                            rowKey={row => String(row.id || row.goodsCode)}
                            data={goods}
                            pagination={false}
                            columns={[
                                { label: "物料名称", minWidth: 206, prop: "goodsName" },
                                { label: "合同量（吨）", minWidth: 178, prop: "totalTon" },
                                { label: "完成量（吨）", minWidth: 212, prop: "completedTon" },
                                {
                                    label: "结算率",
                                    minWidth: 188,
                                    prop: "settlementRate",
                                    render: ({ row }) => numberValue(row.settlementRate)?.toFixed(2) || "--"
                                },
                                {
                                    label: "结算吨数（吨）",
                                    minWidth: 192,
                                    prop: "settlementTon",
                                    render: ({ row }) => formatNumber(row.settlementTon)
                                },
                                {
                                    label: "结算金额（元）",
                                    minWidth: 163,
                                    prop: "settlementAmount",
                                    render: ({ row }) => numberValue(row.settlementAmount)?.toFixed(2) || "--"
                                },
                                {
                                    label: "配比",
                                    minWidth: 181,
                                    prop: "materialRatio",
                                    render: ({ row }) =>
                                        goodsTotalTon > 0
                                            ? `${((Number(row.totalTon || 0) / goodsTotalTon) * 100).toFixed(2)}%`
                                            : "--"
                                },
                                {
                                    label: "最新单价（元/吨）",
                                    minWidth: 180,
                                    prop: "latestUnitPrice",
                                    render: ({ row }) => formatNumber(row.latestUnitPrice ?? row.unitPrice)
                                },
                                {
                                    label: "完成进度",
                                    minWidth: 236,
                                    prop: "completedSchedule",
                                    render: ({ row }) => {
                                        const progress = numberValue(row.completedSchedule);
                                        return progress == null ? "--" : `${(progress * 100).toFixed(0)}%`;
                                    }
                                }
                            ]}
                            summaryMethod={({ columns, data }) =>
                                columns.map(column => {
                                    const property = String(column.property || "");
                                    if (property === "goodsName") return "合计";
                                    if (property === "totalTon") return formatNumber(goodsTotalTon);
                                    if (property === "completedTon") {
                                        return formatNumber(
                                            data.reduce((sum, row) => sum + Number(row.completedTon || 0), 0)
                                        );
                                    }
                                    if (property === "settlementTon") {
                                        return formatNumber(
                                            data.reduce((sum, row) => sum + Number(row.settlementTon || 0), 0)
                                        );
                                    }
                                    if (property === "settlementAmount") {
                                        return data
                                            .reduce((sum, row) => sum + Number(row.settlementAmount || 0), 0)
                                            .toFixed(2);
                                    }
                                    return "--";
                                })
                            }
                        />
                    </div>
                    <div className="section-card zone-contract-booking">
                        <Space>
                            <Typography.Title level={5}>预约明细</Typography.Title>
                        </Space>
                        <ZhTable
                            border
                            rowKey={row =>
                                `${String(reservationCore(row).id)}-${String(reservationGoods(row).goodsCode ?? "")}`
                            }
                            data={reservations}
                            pagination={false}
                            columns={[
                                ...reservationColumns.slice(0, 3),
                                {
                                    label: "预到日期",
                                    minWidth: 180,
                                    prop: "reservationTime",
                                    render: ({ row }) =>
                                        displayApiDateTimeUnknown(reservationValue(row, "reservationTime"))
                                },
                                {
                                    label: "物料名称",
                                    minWidth: 120,
                                    prop: "goodsCode",
                                    render: ({ row }) =>
                                        goodsIntroByGoodsCode(numberValue(reservationGoods(row).goodsCode))
                                },
                                {
                                    label: "计划量（吨）",
                                    minWidth: 110,
                                    prop: "plannedTonnage",
                                    render: ({ row }) => formatNumber(reservationGoods(row).plannedTonnage)
                                },
                                {
                                    label: "装货吨数（吨）",
                                    minWidth: 130,
                                    prop: "loadingTonnage",
                                    render: ({ row }) => formatNumber(reservationGoods(row).loadingTonnage)
                                },
                                {
                                    label: "结算率",
                                    minWidth: 90,
                                    prop: "settlementRate",
                                    render: ({ row }) => formatNumber(reservationGoods(row).settlementRate, 2)
                                },
                                {
                                    label: "结算吨数（吨）",
                                    minWidth: 120,
                                    prop: "settlementTon",
                                    render: ({ row }) => formatNumber(reservationGoods(row).settlementTon)
                                },
                                {
                                    label: "结算单价（元）",
                                    minWidth: 120,
                                    prop: "unitPrice",
                                    render: ({ row }) => formatNumber(reservationGoods(row).unitPrice)
                                },
                                {
                                    label: "结算金额（元）",
                                    minWidth: 120,
                                    prop: "settlementAmount",
                                    render: ({ row }) => formatNumber(reservationGoods(row).settlementAmount, 2)
                                },
                                {
                                    label: "预约单状态",
                                    minWidth: 100,
                                    prop: "state",
                                    render: ({ row }) =>
                                        reservationOrderStateLabel(Number(reservationValue(row, "state")))
                                },
                                {
                                    label: "创建时间",
                                    minWidth: 180,
                                    prop: "createdTime",
                                    render: ({ row }) => displayApiDateTimeUnknown(reservationValue(row, "createdTime"))
                                }
                            ]}
                        />
                    </div>
                    <div className="section-card zone-contract-history">
                        {core?.id != null ? (
                            <OperationHistoryPanel
                                bizNo={core.id}
                                emptyText="暂无历史记录"
                                hideFrame
                                module={Operation_History_Enum.ZONE_CONTRACT_DETAIL}
                                title="历史记录"
                            />
                        ) : null}
                        <Space className="operation-fixed-btn">
                            {!cancelled && canManage ? (
                                <Button type="primary" onClick={() => void openAssign()}>
                                    {core?.handlerId ? "变更指派" : "指派"}
                                </Button>
                            ) : null}
                            {!cancelled && canDeal ? (
                                <>
                                    <Button
                                        type="primary"
                                        onClick={() => navigate(`/tradeManage/zoneContract/edit/${core?.id}`)}
                                    >
                                        编辑
                                    </Button>
                                    <Button danger onClick={() => void requestCancel()}>
                                        取消合同
                                    </Button>
                                </>
                            ) : null}
                            <Button onClick={() => navigate(-1)}>返回</Button>
                        </Space>
                    </div>
                </>
            )}
            <Modal
                open={cancelOpen}
                title="取消合同"
                onCancel={() => setCancelOpen(false)}
                onOk={() => void confirmCancel()}
            >
                确认取消合同吗？
            </Modal>
            <AssignDialog
                open={assignOpen}
                title="指派专员"
                info={detail}
                officers={assignOfficers}
                tips="跟进的合同数量"
                countFiled="orderCount"
                curOfficerId={handlerId}
                onClose={() => {
                    setAssignOpen(false);
                    setHandlerId(undefined);
                }}
                onCurOfficerIdChange={setHandlerId}
                onSubmit={submitAssign}
            />
            <Modal open={priceOpen} title="单价调整日志" footer={null} onCancel={() => setPriceOpen(false)}>
                <ZhTable
                    border
                    rowKey={row => String(row.id)}
                    data={logs}
                    columns={logColumns}
                    pagination={false}
                />
            </Modal>
        </section>
    );
}

export default ZoneContractDetailPage;
